/**
 * Recruitment ATS — shared loading / empty / error states.
 * Matches the dashboard patterns: gray-circle empty state and pale-red error banner.
 */

import React from 'react';
import { Search, AlertCircle, RefreshCw } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { Button } from '../../../components/ui/Button';

/** Centered empty state — gray circle + icon + title + subline. */
export const EmptyState: React.FC<{
	title?: string;
	message?: string;
	icon?: React.ReactNode;
	action?: React.ReactNode;
	className?: string;
}> = ({ title = 'No results found', message, icon, action, className }) => (
	<div className={cn('flex flex-col items-center justify-center px-6 py-16 text-center', className)}>
		<div className='mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 text-gray-400'>
			{icon ?? <Search className='h-7 w-7' />}
		</div>
		<h3 className='text-base font-semibold text-gray-900'>{title}</h3>
		{message && <p className='mt-1 max-w-md text-sm text-gray-500'>{message}</p>}
		{action && <div className='mt-4'>{action}</div>}
	</div>
);

/** Pale-red error banner with an optional retry. */
export const ErrorState: React.FC<{
	message?: string;
	onRetry?: () => void;
	className?: string;
}> = ({ message = 'Something went wrong while loading this. Please try again.', onRetry, className }) => (
	<div
		role='alert'
		className={cn(
			'flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700',
			className
		)}
	>
		<AlertCircle className='mt-0.5 h-4 w-4 shrink-0' />
		<div className='flex-1'>
			<p className='font-medium'>{message}</p>
		</div>
		{onRetry && (
			<Button variant='outline' size='sm' onClick={onRetry} leftIcon={<RefreshCw className='h-3.5 w-3.5' />}>
				Retry
			</Button>
		)}
	</div>
);

/** Inline field/form error line (pale red). */
export const InlineError: React.FC<{ message: string; className?: string }> = ({ message, className }) => (
	<div
		role='alert'
		className={cn('rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600', className)}
	>
		{message}
	</div>
);
